"use client"

import React, { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { cn } from '@/shared/utils/tw'
import type { ChatMessageType } from '@/features/text-chat-with-agent/types/chat-message'
import type { SupportedKioskLanguage } from '../types/kiosk-config'

/**
 * 키오스크 대화 내역 모달
 *
 * Figma 디자인: node-id=2422-118
 * - 반투명 어두운 오버레이: rgba(18, 19, 20, 0.6)
 * - 에이전트 메시지: 아바타(232x232px) + 말풍선
 * - 유저 메시지: 말풍선만 (우측 정렬)
 * - 말풍선: rgba(255, 255, 255, 0.9), backdrop-blur 100px
 * - 에이전트 말풍선 borderRadius: 100px 100px 100px 20px
 * - 유저 말풍선 borderRadius: 100px 100px 20px 100px
 * - 닫기 버튼: 하단 중앙 "닫기" 텍스트 버튼
 */

// 다국어 텍스트
const EMPTY_MESSAGE_TEXT: Record<SupportedKioskLanguage, string> = {
  ko: '아직 대화가 없습니다',
  en: 'No conversation yet',
  zh: '暂无对话',
  ja: 'まだ会話がありません',
}

export interface KioskChatHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  messages: ChatMessageType[]
  /** 아바타 프로필 이미지 URL */
  avatarImageUrl?: string
  /** 선택된 언어 */
  selectedLanguage: SupportedKioskLanguage
}

// Figma 기준 사이즈 (2160 x 3840)
const FIGMA_WIDTH = 2160
const FIGMA_HEIGHT = 3840

export const KioskChatHistoryModal = ({
  isOpen,
  onClose,
  messages,
  avatarImageUrl = '/webp/nani-profile.webp',
  selectedLanguage,
}: KioskChatHistoryModalProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isInitialRenderRef = useRef(true)
  const [isAtBottom, setIsAtBottom] = useState(true)

  // 스크롤 위치 감지
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return
    const threshold = 100
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold
    setIsAtBottom(atBottom)
  }, [])

  // 스크롤 이벤트 리스너
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!isOpen || !container) return
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [isOpen, handleScroll])

  // 통합 스크롤 로직: 모달 열릴 때/메시지 업데이트 시
  useEffect(() => {
    if (!isOpen || !scrollContainerRef.current) return

    const container = scrollContainerRef.current

    // 모달 처음 열릴 때는 맨 아래로 스크롤
    if (isInitialRenderRef.current) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
          setIsAtBottom(true)
        }
      }, 100)
      isInitialRenderRef.current = false
      return
    }

    // 스트리밍 업데이트 시: 하단 근처에 있으면 자동 스크롤
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200
    if (isNearBottom) {
      container.scrollTop = container.scrollHeight
    }
  }, [isOpen, messages])

  // 모달 닫힐 때 초기 렌더링 플래그 리셋
  useEffect(() => {
    if (!isOpen) {
      isInitialRenderRef.current = true
      setIsAtBottom(true)
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center"
      onClick={(e) => {
        e.stopPropagation() // 이벤트 전파 차단 (부모의 handleScreenTouch 방지)
        onClose()
      }}
    >
      {/* 반투명 어두운 오버레이 */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(18, 19, 20, 0.8)' }}
      />

      {/* 모달 컨테이너 - 9:16 비율 유지, flex column 레이아웃 */}
      <div
        className="relative h-full mx-auto flex flex-col"
        style={{
          aspectRatio: '9/16',
          maxHeight: '100%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 메시지 목록 - 스크롤 영역 + 그라데이션 래퍼 */}
        <div className="relative flex-1 min-h-0">
          <div
            ref={scrollContainerRef}
            className="h-full overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            style={{
              // 상단 여백 (Figma: 255px)
              paddingTop: `${(279 / FIGMA_HEIGHT) * 100}%`,
              // 좌우 여백
              paddingLeft: `${(73 / FIGMA_WIDTH) * 100}%`,
              paddingRight: `${(67 / FIGMA_WIDTH) * 100}%`,
              // 스크롤 중: 하단 페이드 / 스크롤 끝: 마스크 해제
              ...(isAtBottom ? {} : {
                maskImage: 'linear-gradient(to bottom, black 88%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 88%, transparent 100%)',
              }),
            }}
          >
            <div
              className="flex flex-col"
              style={{
                gap: `${(60 / FIGMA_HEIGHT) * 100}cqh`,
                paddingBottom: `${(100 / FIGMA_HEIGHT) * 100}cqh`,
              }}
            >
              {messages.map((msg, index) => (
                <MessageBubble
                  key={msg.turnId || `msg-${index}-${msg.sender}-${msg.message?.slice(0, 10) || 'empty'}`}
                  message={msg}
                  avatarImageUrl={avatarImageUrl}
                />
              ))}

              {/* 메시지가 없을 때 */}
              {messages.length === 0 && (
                <div
                  className="flex items-center justify-center text-white/50 font-pretendard"
                  style={{
                    fontSize: `${(60 / FIGMA_WIDTH) * 100}cqw`,
                    minHeight: '50cqh',
                  }}
                >
                  {EMPTY_MESSAGE_TEXT[selectedLanguage]}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* 하단 여백: 스크롤 영역이 닫기 버튼과 겹치지 않도록 (Figma: 채팅영역 y=3307 끝, 닫기버튼 y=3433 시작) */}
        <div
          className="flex-shrink-0"
          style={{ height: `${((FIGMA_HEIGHT - 3347) / FIGMA_HEIGHT) * 100}%` }}
        />
      </div>
    </div>
  )
}

/**
 * 메시지 말풍선 컴포넌트
 */
interface MessageBubbleProps {
  message: ChatMessageType
  avatarImageUrl: string
}

const MessageBubble = ({ message, avatarImageUrl }: MessageBubbleProps) => {
  const isAgent = message.sender === 'agent'

  // Figma 기준 사이즈
  const avatarSize = (220 / FIGMA_WIDTH) * 100 // cqw 단위 (Figma: 220x220)
  const gap = (48 / FIGMA_WIDTH) * 100 // cqw 단위 (Figma: 48px)

  return (
    <div
      className={cn(
        'flex',
        isAgent ? 'flex-row items-end' : 'flex-row-reverse items-end'
      )}
      style={{
        gap: `${gap}cqw`,
      }}
    >
      {/* 에이전트 아바타 (에이전트 메시지일 때만) */}
      {isAgent && (
        <div
          className="relative flex-shrink-0 rounded-full overflow-hidden"
          style={{
            width: `${avatarSize}cqw`,
            height: `${avatarSize}cqw`,
            border: `${(10 / FIGMA_WIDTH) * 100}cqw solid rgba(255, 255, 255, 0.05)`,
            background: 'rgba(255, 255, 255, 0.1)'
          }}
        >
          <Image
            src={avatarImageUrl}
            alt="Avatar"
            fill
            className="object-cover"
          />
        </div>
      )}

      {/* 말풍선 */}
      <div
        className={cn(
          'relative',
          isAgent ? 'max-w-[75%]' : 'max-w-[70%]'
        )}
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(100px)',
          // Figma: 에이전트 40/60/64/80, 유저 40/60/40/80 → 상하 균등 패딩으로 텍스트 중앙 정렬
          padding: isAgent
            ? `${(52 / FIGMA_HEIGHT) * 100}cqh ${(60 / FIGMA_WIDTH) * 100}cqw ${(52 / FIGMA_HEIGHT) * 100}cqh ${(80 / FIGMA_WIDTH) * 100}cqw`
            : `${(40 / FIGMA_HEIGHT) * 100}cqh ${(60 / FIGMA_WIDTH) * 100}cqw ${(40 / FIGMA_HEIGHT) * 100}cqh ${(80 / FIGMA_WIDTH) * 100}cqw`,
          // 에이전트: 좌하단만 작게 / 유저: 우하단만 작게
          borderRadius: isAgent
            ? `${(100 / FIGMA_WIDTH) * 100}cqw ${(100 / FIGMA_WIDTH) * 100}cqw ${(100 / FIGMA_WIDTH) * 100}cqw ${(20 / FIGMA_WIDTH) * 100}cqw`
            : `${(100 / FIGMA_WIDTH) * 100}cqw ${(100 / FIGMA_WIDTH) * 100}cqw ${(20 / FIGMA_WIDTH) * 100}cqw ${(100 / FIGMA_WIDTH) * 100}cqw`,
        }}
      >
        <p
          className="font-pretendard font-medium text-black whitespace-pre-wrap break-words break-keep"
          style={{
            fontSize: `${(68 / FIGMA_WIDTH) * 100}cqw`,
            lineHeight: '1.6',
            letterSpacing: '-0.01em',
          }}
        >
          {message.message || ''}
          {/* 스트리밍 중일 때 커서 표시 */}
          {message.isStreaming && (
            <span className="inline-block w-[2px] h-[1em] bg-black/50 ml-1 animate-pulse" />
          )}
        </p>
      </div>
    </div>
  )
}

export default KioskChatHistoryModal
